import React, { useState } from 'react';
import { FaMoneyBillWave } from 'react-icons/fa';
import { toast } from 'sonner';
import { axiosInstance as axios } from '../../context/AuthContext';

import { BudgetOverview } from './budget/BudgetOverview';
import { CreateBudgetModal } from './budget/CreateBudgetModal';
import { ExpenseModal, EMPTY_EXPENSE } from './budget/ExpenseModal';
import { DeleteExpenseDialog } from './budget/DeleteExpenseDialog';
import { AddContributionModal } from './budget/AddContributionModal';
import { OnlineContributionModal } from './budget/OnlineContributionModal';

const API = '/api';

function BudgetTab({ budget, eventId, familyMembers, familyId, onBudgetUpdated }) {
  const [showCreateBudget, setShowCreateBudget] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showContributionForm, setShowContributionForm] = useState(false);
  const [showOnlineContributionForm, setShowOnlineContributionForm] = useState(false);

  const [totalBudget, setTotalBudget] = useState('');
  const [expenseData, setExpenseData] = useState(EMPTY_EXPENSE);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [savingExpense, setSavingExpense] = useState(false);
  const [showEditGoal, setShowEditGoal] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [contributionData, setContributionData] = useState({ contributor: '', amount: '' });
  const [onlineContributionAmount, setOnlineContributionAmount] = useState('');
  const [onlineContributionProcessing, setOnlineContributionProcessing] = useState(false);

  const handleCreateBudget = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/budgets`,
        { event_id: eventId, total_budget: parseFloat(totalBudget) },
        { withCredentials: true }
      );
      setShowCreateBudget(false);
      setTotalBudget('');
      onBudgetUpdated();
    } catch (error) {
      console.error('Error creating budget:', error);
      alert('Failed to create budget');
    }
  };

  const closeExpenseForm = () => {
    setShowExpenseForm(false);
    setEditingExpenseId(null);
    setExpenseData(EMPTY_EXPENSE);
  };

  const openEditExpense = (expense) => {
    setExpenseData({
      description: expense.description,
      amount: String(expense.amount),
      paid_by: expense.paid_by || '',
      category: expense.category || 'other',
      status: expense.status || 'paid'
    });
    setEditingExpenseId(expense.expense_id);
    setShowExpenseForm(true);
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    setSavingExpense(true);
    const payload = { ...expenseData, amount: parseFloat(expenseData.amount) };
    try {
      if (editingExpenseId) {
        await axios.patch(`${API}/budgets/${budget.budget_id}/expenses/${editingExpenseId}`, payload, { withCredentials: true });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/budgets/${budget.budget_id}/expenses`, payload, { withCredentials: true });
        toast.success('Expense added');
      }
      closeExpenseForm();
      onBudgetUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteExpense = async () => {
    const expense = expenseToDelete;
    setExpenseToDelete(null);
    try {
      await axios.delete(`${API}/budgets/${budget.budget_id}/expenses/${expense.expense_id}`, { withCredentials: true });
      toast.success('Expense deleted');
      onBudgetUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleUpdateGoal = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/budgets/${budget.budget_id}`, { total_budget: parseFloat(goalInput) }, { withCredentials: true });
      toast.success('Budget goal updated');
      setShowEditGoal(false);
      onBudgetUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update budget goal');
    }
  };

  const handleAddContribution = async (e) => {
    e.preventDefault();

    if (!contributionData.contributor || !contributionData.amount) {
      alert('Please select a contributor and enter an amount');
      return;
    }

    try {
      await axios.post(
        `${API}/budgets/${budget.budget_id}/contributions`,
        {
          user_id: contributionData.contributor,
          amount: parseFloat(contributionData.amount)
        },
        { withCredentials: true }
      );
      setShowContributionForm(false);
      setContributionData({ contributor: '', amount: '' });
      onBudgetUpdated();
    } catch (error) {
      console.error('Error adding contribution:', error);
      alert('Failed to add contribution');
    }
  };

  const handleOnlineContribution = async (e) => {
    e.preventDefault();
    setOnlineContributionProcessing(true);

    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        payment_type: 'event_contribution',
        family_id: familyId,
        event_id: eventId,
        amount: parseFloat(onlineContributionAmount),
        notes: 'Online event contribution',
        origin_url: window.location.origin
      }, { withCredentials: true });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to initiate payment');
      setOnlineContributionProcessing(false);
    }
  };

  const sumExpenses = (status) => budget?.expenses?.filter((exp) => (exp.status || 'paid') === status).reduce((sum, exp) => sum + exp.amount, 0) || 0;
  const paidExpenses = sumExpenses('paid');
  const plannedExpenses = sumExpenses('planned');
  const totalContributions = budget?.contributions?.reduce((sum, cont) => sum + cont.amount, 0) || 0;
  const budgetGoal = budget?.total_budget || 0;
  const availableFunds = totalContributions - paidExpenses;
  const fundingProgress = budgetGoal > 0 ? (totalContributions / budgetGoal) * 100 : 0;

  if (!budget) {
    return (
      <div className="bg-white rounded-xl shadow-md p-12 text-center" data-testid="no-budget-message">
        <FaMoneyBillWave className="text-gray-300 text-6xl mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-800 mb-2">No budget set</h3>
        <p className="text-gray-600 mb-6">Create a budget to track expenses and contributions.</p>
        <button
          onClick={() => setShowCreateBudget(true)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          data-testid="create-budget-btn"
        >
          Create Budget
        </button>

        {showCreateBudget && (
          <CreateBudgetModal
            totalBudget={totalBudget}
            setTotalBudget={setTotalBudget}
            onSubmit={handleCreateBudget}
            onClose={() => setShowCreateBudget(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="budget-tab">
      <BudgetOverview
        budget={budget}
        paidExpenses={paidExpenses}
        plannedExpenses={plannedExpenses}
        totalContributions={totalContributions}
        budgetGoal={budgetGoal}
        availableFunds={availableFunds}
        fundingProgress={fundingProgress}
        canManage={budget.can_manage}
        onEditGoal={() => { setGoalInput(String(budgetGoal)); setShowEditGoal(true); }}
        onAddExpense={() => setShowExpenseForm(true)}
        onEditExpense={openEditExpense}
        onDeleteExpense={setExpenseToDelete}
        onAddContribution={() => setShowContributionForm(true)}
        onContributeOnline={() => setShowOnlineContributionForm(true)}
      />

      {showExpenseForm && (
        <ExpenseModal
          mode={editingExpenseId ? 'edit' : 'add'}
          expenseData={expenseData}
          setExpenseData={setExpenseData}
          familyMembers={familyMembers}
          onSubmit={handleSaveExpense}
          onClose={closeExpenseForm}
          saving={savingExpense}
        />
      )}

      <DeleteExpenseDialog
        expense={expenseToDelete}
        onConfirm={handleDeleteExpense}
        onCancel={() => setExpenseToDelete(null)}
      />

      {showEditGoal && (
        <CreateBudgetModal
          title="Edit Budget Goal"
          submitLabel="Save"
          totalBudget={goalInput}
          setTotalBudget={setGoalInput}
          onSubmit={handleUpdateGoal}
          onClose={() => setShowEditGoal(false)}
        />
      )}

      {showContributionForm && (
        <AddContributionModal
          contributionData={contributionData}
          setContributionData={setContributionData}
          familyMembers={familyMembers}
          onSubmit={handleAddContribution}
          onClose={() => {
            setShowContributionForm(false);
            setContributionData({ contributor: '', amount: '' });
          }}
        />
      )}

      {showOnlineContributionForm && (
        <OnlineContributionModal
          amount={onlineContributionAmount}
          setAmount={setOnlineContributionAmount}
          processing={onlineContributionProcessing}
          onSubmit={handleOnlineContribution}
          onClose={() => {
            setShowOnlineContributionForm(false);
            setOnlineContributionAmount('');
          }}
        />
      )}
    </div>
  );
}

export default BudgetTab;
